/**
 * AI Prescription Safety Check API module
 * Steps 16-17 client-side bindings
 */
import { fetchApi } from '../client';

export const prescriptionSafety = {
  /** Run the full AI safety check for a prescription */
  runCheck: (prescriptionId: string, forceRefresh = false) =>
    fetchApi<PrescriptionSafetyResponse>(`/api/ai/prescriptions/${prescriptionId}/safety-check?forceRefresh=${forceRefresh}`, {
      method: 'POST',
    }),

  /** Check which AI features are enabled for the current user's clinic */
  getFeatureStatus: () =>
    fetchApi<{ prescriptionSafetyCheckEnabled: boolean }>('/api/ai/feature-status'),

  /** Doctor approves HIGH/CRITICAL finding */
  approve: (approvalId: string, comments?: string) =>
    fetchApi<void>(`/api/ai/prescriptions/approvals/${approvalId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ comments: comments ?? '' }),
    }),

  /** Doctor rejects/overrides finding (comments required) */
  reject: (approvalId: string, comments: string) =>
    fetchApi<void>(`/api/ai/prescriptions/approvals/${approvalId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ comments }),
    }),

  /** Get pending approvals for the logged-in doctor */
  getPendingApprovals: () =>
    fetchApi<AiSafetyApproval[]>('/api/ai/prescriptions/approvals/pending'),
};

// ─────────────────────── Types ───────────────────────────────────────────────

export type AiRiskLevel = 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL';
export type AiRequestStatus =
  | 'SUCCESS'
  | 'FAILED'
  | 'CACHE_HIT'
  | 'BLOCKED_BY_RULE_ENGINE'
  | 'QUOTA_EXCEEDED'
  | 'PERMISSION_DENIED';

export interface SafetyFinding {
  code: string;
  severity: AiRiskLevel;
  message: string;
  medicationName: string | null;
  source: 'RULE_ENGINE' | 'AI';
}

export interface PrescriptionSafetyResponse {
  auditLogId: string;
  finalRisk: AiRiskLevel;
  ruleEngineRisk: AiRiskLevel;
  aiRisk: AiRiskLevel;
  status: AiRequestStatus;
  safetyNarrative: string;
  doctorApprovalRequired: boolean;
  approvalId: string | null;
  findings: SafetyFinding[];
  recommendations: string[];
  cacheHit: boolean;
}

export interface AiSafetyApproval {
  id: string;
  prescriptionId: string;
  riskLevel: AiRiskLevel;
  approvalStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  doctorComments: string | null;
  createdAt: string;
}
